import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  createTicket, uploadTicketAttachment, updateTicketAttachments,
  TICKET_CATEGORIES, TICKET_PRIORITIES,
} from '../services/helpdeskService';
import '../pages/Helpdesk.css';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 25 * 1024 * 1024;

export default function HelpdeskTicketForm({ open, onClose, onCreated, initialCategory = '', initialSubject = '' }) {
  const { profile } = useAuth();
  const [subject, setSubject] = useState(initialSubject || '');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(
    initialCategory && TICKET_CATEGORIES.some((c) => c.value === initialCategory) ? initialCategory : 'other',
  );
  const [priority, setPriority] = useState('medium');
  const [relatedPatient, setRelatedPatient] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const hasDraft = () => Boolean(subject.trim() || description.trim() || relatedPatient.trim() || files.length > 0);

  const handleClose = () => {
    if (submitting) return;
    if (hasDraft() && !window.confirm('Discard this ticket draft?')) return;
    onClose?.();
  };

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter((f) => f.size <= MAX_FILE_SIZE).slice(0, MAX_FILES);
    if (selected.length > MAX_FILES) setError(`Max ${MAX_FILES} files allowed.`);
    else if (selected.some((f) => f.size > MAX_FILE_SIZE)) setError('One or more files exceed 25MB.');
    else setError('');
    setFiles(valid);
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { id, ticketNumber } = await createTicket({
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
        relatedPatientId: null,
        relatedPatientName: relatedPatient.trim() || null,
        submittedBy: {
          uid: profile.uid,
          displayName: profile.displayName || '',
          email: profile.email || '',
          role: profile.role || '',
        },
        attachments: [],
      });

      if (files.length > 0) {
        try {
          const attachments = [];
          for (const f of files) {
            const att = await uploadTicketAttachment(id, f);
            attachments.push(att);
          }
          await updateTicketAttachments(id, attachments);
        } catch (uploadErr) {
          setSubmitting(false);
          setError(
            `Ticket #${ticketNumber} was created, but attachments failed to upload `
            + `(${uploadErr?.message || 'unknown error'}). You can add them from the ticket detail.`,
          );
          onCreated?.(ticketNumber);
          return;
        }
      }

      setSubmitting(false);
      onCreated?.(ticketNumber);
    } catch (e) {
      setError(e?.message || 'Failed to create ticket.');
      setSubmitting(false);
    }
  };

  return (
    <div className="helpdesk-modal-overlay" onClick={handleClose}>
      <div className="helpdesk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="helpdesk-modal-header">
          <h3>New Ticket</h3>
          <button className="helpdesk-modal-close" onClick={handleClose}>×</button>
        </div>
        <div className="helpdesk-modal-body">
          <div className="helpdesk-form-group">
            <label className="helpdesk-form-label">Subject *</label>
            <input className="helpdesk-form-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary of the issue" />
          </div>
          <div className="helpdesk-form-group">
            <label className="helpdesk-form-label">Description *</label>
            <textarea className="helpdesk-form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the issue in detail" rows={5} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="helpdesk-form-group" style={{ flex: 1 }}>
              <label className="helpdesk-form-label">Category</label>
              <select className="helpdesk-form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                {TICKET_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="helpdesk-form-group" style={{ flex: 1 }}>
              <label className="helpdesk-form-label">Priority</label>
              <select className="helpdesk-form-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {TICKET_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="helpdesk-form-group">
            <label className="helpdesk-form-label">Related Patient (optional)</label>
            <input className="helpdesk-form-input" value={relatedPatient} onChange={(e) => setRelatedPatient(e.target.value)} placeholder="Patient name" />
          </div>
          <div className="helpdesk-form-group">
            <label className="helpdesk-form-label">Attachments (max {MAX_FILES}, 25MB each)</label>
            <input type="file" multiple onChange={handleFiles} style={{ fontSize: '0.82rem' }} />
            <div className="helpdesk-file-guidance">Screenshots are preferred for faster resolution.</div>
            {files.length > 0 && (
              <ul className="helpdesk-file-list">
                {files.map((f, i) => <li key={i}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</li>)}
              </ul>
            )}
          </div>
          {error && <div style={{ color: 'var(--tk-text-dc2626)', fontSize: '0.82rem', marginBottom: '0.5rem' }}>{error}</div>}
        </div>
        <div className="helpdesk-modal-actions">
          <button className="helpdesk-form-cancel" onClick={handleClose}>Cancel</button>
          <button className="helpdesk-form-submit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}
